/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef,
  inject,
  AfterViewInit,
  NgZone,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  computed,
  signal,
  HostListener,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import {
  HeaderComponent,
  NebulaNavigationService,
  DialogService,
  NebulaToastService,
  NebulaAccionesSlot,
} from 'nebula-ui-kit';
import { LucideAngularModule } from 'lucide-angular';
import {
  TranslationService,
  TranslatePipe,
  AuthService,
  PerfilService,
  PerfilData,
  PerfilModalLauncher,
  SystemSyncTopbarDateService,
  DirtyStateService,
  EstadoDeTrabajoService,
  TabIdService,
  UiProfileService,
  resolveEnvKey,
  quitarContextoDeProducto,
  leerDeLaPestana,
} from '@nebula-shared/data-access';
import { Subject } from 'rxjs';
import {
  GlobalMenuSearchService,
  MenuSearchOption,
} from '../../../../data-access/src/lib/global-menu-search.service';
import { BandejaTareasComponent } from '../centro-tareas/bandeja-tareas.component';

@Component({
  selector: 'app-topbar-wrapper',
  templateUrl: './topbar-wrapper.component.html',
  styleUrls: ['./topbar-wrapper.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    HeaderComponent,
    NebulaAccionesSlot,
    LucideAngularModule,
    TranslatePipe,
    BandejaTareasComponent,
  ],
})
export class TopbarWrapperComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly navService = inject(NebulaNavigationService);
  private readonly ngZone = inject(NgZone);
  private readonly systemDateService = inject(SystemSyncTopbarDateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly menuSearch = inject(GlobalMenuSearchService);
  private readonly translation = inject(TranslationService);
  private readonly elementRef = inject(ElementRef);
  private readonly perfilService = inject(PerfilService);
  private readonly perfilModalLauncher = inject(PerfilModalLauncher);
  private readonly authService = inject(AuthService);

  private readonly dirtyState = inject(DirtyStateService);
  private readonly estadoDeTrabajo = inject(EstadoDeTrabajoService);
  private readonly tabIdService = inject(TabIdService);
  private readonly dialogService = inject(DialogService);

  private readonly destroy$ = new Subject<void>();

  // ========================================
  // PROPIEDADES EXISTENTES
  // ========================================
  readonly userFullName = signal<string>('');
  readonly userRoles = signal<string>('');

  readonly languages = this.translation.availableLanguages;
  readonly activeLang = signal<string>(this.translation.currentLang);
  readonly langMenuOpen = signal<boolean>(false);
  readonly langMenuTop = signal<number>(0);
  readonly langMenuRight = signal<number>(8);

  readonly activeFlag = computed(() => {
    const code = this.activeLang();
    const lang = this.languages().find((l) => l.code === code);
    return lang?.flagEmoji || code.toUpperCase();
  });

  toggleLangMenu(): void {
    const willOpen = !this.langMenuOpen();
    if (willOpen) {
      const host = this.elementRef.nativeElement as HTMLElement;
      const btn = host.querySelector('.lang-btn') as HTMLElement | null;
      if (btn && typeof window !== 'undefined') {
        const r = btn.getBoundingClientRect();
        this.langMenuTop.set(Math.round(r.bottom + 6));
        this.langMenuRight.set(Math.max(8, Math.round(window.innerWidth - r.right)));
      }
    }
    this.langMenuOpen.set(willOpen);
  }

  async selectLanguage(code: string): Promise<void> {
    this.langMenuOpen.set(false);
    await this.translation.setLanguage(code);
    this.activeLang.set(code);
  }

  profileItemLabel(item: { id: number; action: string }): string {
    const key = item.id === 3 ? 'NAV.SIGN_OUT' : item.id === 1 ? 'TITLE.SELECT_PRODUCTS' : '';
    return key ? this.translation.translate(key) : item.action;
  }

  // ========================================
  // MENU DE USUARIO · PERFIL 3.0
  // ========================================
  private readonly uiProfile = inject(UiProfileService);

  /** Ambiente en que corre la aplicacion (dev, qa o prod), el mismo que viaja en cada peticion. */
  readonly ambiente = resolveEnvKey();

  /** Si el menu se pinta con el diseño del perfil 3.0; el perfil vigente conserva el suyo. */
  esPerfil30(): boolean {
    return this.uiProfile.esPerfil30();
  }

  /** Iniciales del avatar del panel: nombre y apellido, o la primera letra del login. */
  iniciales(): string {
    const partes = (this.userFullName() || this.userName).trim().split(/\s+/).filter(Boolean);
    const letras = partes.length > 1 ? partes[0][0] + partes[1][0] : (partes[0]?.[0] ?? '');
    return letras.toUpperCase();
  }

  /**
   * Compañia de la sesion DE ESTA PESTAÑA, la misma que muestra el pie de pagina. Si otra pestaña
   * elige otra compania, esta conserva la suya: es la de su token.
   */
  companiaSesion(): string {
    try {
      return leerDeLaPestana('companyName') ?? '';
    } catch {
      return '';
    }
  }

  /** Opciones del menu salvo la salida, que el perfil 3.0 separa y pinta en rojo. */
  opcionesPerfil(): Array<{ id: number; action: string }> {
    return this.itemsProfileMenu.filter((item) => item.id !== TopbarWrapperComponent.SALIR);
  }

  /** La opcion de cerrar sesion, si el menu la trae. */
  opcionSalir(): { id: number; action: string } | undefined {
    return this.itemsProfileMenu.find((item) => item.id === TopbarWrapperComponent.SALIR);
  }

  /** Icono de cada opcion del menu, por su identificador. */
  iconoOpcion(item: { id: number }): string {
    return item.id === 1 ? 'layout-grid' : 'user';
  }

  /** Identificador de la opcion de cerrar sesion en `PROFILE_MENU_ITEMS`. */
  private static readonly SALIR = 3;

  readonly searchResults = signal<MenuSearchOption[]>([]);
  readonly showSearchResults = signal<boolean>(false);
  /** Los avisos del buscador salen en la zona de toast del kit (Mantis #212). */
  private readonly toast = inject(NebulaToastService);

  @Input() userName = '';
  @Input() isDarkMode = false;
  @Input() logoUrl = '';
  @Input() itemsProfileMenu: Array<{ id: number; action: string }> = [];

  @Input() showSearch = true;
  @Input() showNotifications = true;
  @Input() showRefresh = true;
  @Input() showSystemDate = true;
  @Input() showProducts = true;

  @Output() darkModeToggle = new EventEmitter<void>();
  @Output() profileClick = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;

  systemDate = computed(() => this.systemDateService.fechaSistemaFormatted());
  isLoadingSystemDate = computed(() => this.systemDateService.isLoading());

  private readonly MENU_WIDTH = 170;
  private readonly MENU_OFFSET = 8;
  private readonly OVERLAY_RIGHT_MARGIN = 26.67;
  private isMenuOpening = false;

  // ========================================
  // NG LIFECYCLE
  // ========================================
  ngOnInit(): void {
    if (!this.systemDateService.fechaSistema()) {
      this.systemDateService.loadSystemDate();
    }

    this.menuSearch.loadOptions();
    this.cargarPerfilMenu();
  }

  private cargarPerfilMenu(): void {
    const roles = this.authService.getUserRoles() ?? [];
    this.userRoles.set(
      roles
        .map((r: any) => {
          const key = r?.name || r?.authority || '';
          const traducido = this.translation.translate(key);
          return traducido && traducido !== key ? traducido : r?.description || key;
        })
        .filter((label: string) => !!label)
        .join(', '),
    );
    this.perfilService.getMiPerfil().subscribe({
      next: (perfil) => this.setFullName(perfil),
      error: () => this.userFullName.set(''),
    });
  }

  private setFullName(perfil: PerfilData): void {
    this.userFullName.set(`${perfil?.nombres ?? ''} ${perfil?.apellidos ?? ''}`.trim());
  }

  private abrirPerfil(): void {
    this.perfilModalLauncher.open().subscribe((result) => {
      if (result) {
        this.setFullName(result);
      }
    });
  }

  onGlobalSearch(query: string): void {
    const q = (query || '').trim();
    if (q.length < 2) {
      this.searchResults.set([]);
      this.showSearchResults.set(false);
      return;
    }
    this.searchResults.set(this.menuSearch.search(q));
    this.showSearchResults.set(true);
    this.cdr.markForCheck();
  }

  onSelectSearchResult(option: MenuSearchOption): void {
    const result = this.menuSearch.openOption(option);
    if (result === 'limit') {
      this.toast.warning(this.translation.translate('LAYOUT.MAX_TABS', { 0: 8 }));
      return;
    }
    this.closeSearchResults();
  }

  closeSearchResults(): void {
    this.showSearchResults.set(false);
    this.searchResults.set([]);
  }

  @HostListener('document:keydown', ['$event'])
  onSearchKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      const input = this.elementRef.nativeElement.querySelector(
        'lib-header input',
      ) as HTMLInputElement | null;
      input?.focus();
    }
    if (event.key === 'Escape' && this.showSearchResults()) {
      event.preventDefault();
      this.closeSearchResults();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const outside = !this.elementRef.nativeElement.contains(event.target);
    if (outside && this.showSearchResults()) {
      this.closeSearchResults();
    }
    if (outside && this.langMenuOpen()) {
      this.langMenuOpen.set(false);
    }
  }

  ngAfterViewInit(): void {
    if (this.triggerButton?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        const btn = this.triggerButton.nativeElement;
        btn.style.opacity = '0';
        btn.style.pointerEvents = 'none';
      });
    }
    this.relocateLangSelector();
  }

  private relocateLangSelector(attempt = 0): void {
    setTimeout(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const selector = host.querySelector('.lang-selector') as HTMLElement | null;
      const profile =
        (host.querySelector('lib-header .user-profile') as HTMLElement | null) ||
        (document.querySelector('lib-header .user-profile') as HTMLElement | null);

      if (selector && profile?.parentElement) {
        if (selector.nextElementSibling !== profile) {
          profile.parentElement.insertBefore(selector, profile);
        }
        return;
      }
      if (attempt < 20) {
        this.relocateLangSelector(attempt + 1);
      }
    }, 50);
  }

  showMenu(headerRef?: any): void {
    if (this.isMenuOpening) return;
    this.isMenuOpening = true;

    Promise.resolve().then(() => this.profileClick.emit());

    let targetElement: HTMLElement | null = null;
    if (headerRef?.elementRef?.nativeElement) {
      targetElement = headerRef.elementRef.nativeElement.querySelector('.user-profile');
    }
    if (!targetElement) {
      targetElement = document.querySelector('.user-profile');
    }

    if (!targetElement || !this.triggerButton?.nativeElement || !this.menuTrigger) {
      this.isMenuOpening = false;
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const button = this.triggerButton.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      button.removeAttribute('style');
      button.style.position = 'fixed';
      button.style.top = `${rect.bottom + this.MENU_OFFSET}px`;
      button.style.left = `${rect.right - this.MENU_WIDTH}px`;
      button.style.width = `${this.MENU_WIDTH}px`;
      button.style.height = '1px';
      button.style.opacity = '0';
      button.style.pointerEvents = 'none';
      button.style.zIndex = '-1';
    });

    this.menuTrigger.updatePosition();

    setTimeout(() => {
      this.ngZone.run(() => {
        if (this.menuTrigger && !this.menuTrigger.menuOpen) {
          this.menuTrigger.openMenu();
        }
        this.isMenuOpening = false;
      });
    }, 0);
  }

  closeMenu(): void {
    if (this.menuTrigger?.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  onDarkModeToggle(): void {
    this.darkModeToggle.emit();
  }

  /**
   * Maneja las acciones del perfil con verificación de cambios sin guardar
   * Si hay cambios, muestra un diálogo de confirmación y SOLO permite continuar si el usuario confirma
   */
  async onProfileAction(item: { id: number; action: string }): Promise<void> {
    this.closeMenu();

    const currentTabId = this.getCurrentTabId();
    const hasDirty = this.dirtyState.isDirtySync(currentTabId);

    switch (item.id) {
      case 1: // Seleccionar productos
        await this.productsClick();
        break;

      case 2: // Perfil
        this.abrirPerfil();
        break;

      case 3: // Cerrar sesión
        if (hasDirty) {
          const confirmed = await this.showDirtyConfirmDialog();
          if (!confirmed) {
            return;
          }
          this.dirtyState.clear(currentTabId);
        }
        this.onLogout();
        break;

      default:
        break;
    }
  }

  /**
   *  Selecciona productos y limpia el estado de navegación
   */
  onSelectProducts(): void {
    const keysToRemove = [
      'nebula:activeRoute:accounting',
      'nebula:activeRoute:budget',
      'nebula:activeRoute:tesorería',
      'nebula:activeRoute:vault',
      'nebula:activeRoute:invoicing',
      'nebula_ui_nav_v1',
      'selectedRoute',
      'selectedProductName',
      'nebula:navigation:state',
    ];

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
    // La ruta del modulo tambien vive en la pestaña (Mantis #455).
    quitarContextoDeProducto('selectedRoute');

    this.navService.clearTabs();
    this.navService.setContext('clean');
    this.router.navigate(['/layout/products']);
  }

  /**
   *  Cierra sesión
   */
  onLogout(): void {
    this.logout.emit();
  }

  /**
   *  Maneja el click en el botón de productos con verificación de cambios
   * Si hay cambios, muestra un diálogo de confirmación y SOLO permite continuar si el usuario confirma
   */
  async productsClick(): Promise<void> {
    // Salir del producto descarta lo que quede a medias en CUALQUIER pestaña, no solo en la
    // activa: por eso se pregunta si alguna tiene cambios. Al volver a entrar se empieza de cero.
    if (this.estadoDeTrabajo.hayCambiosSinGuardar()) {
      const confirmed = await this.showDirtyConfirmDialog();
      if (!confirmed) {
        return;
      }
    }

    this.onSelectProducts();
  }

  /**
   *  Muestra un diálogo de confirmación para cambios sin guardar usando DialogService
   * @returns Promise<boolean> - true si el usuario confirma, false si cancela
   */
  private showDirtyConfirmDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const title = this.translation.translate('LAYOUT.COMMON.TITLE.UNSAVED_CHANGES');
      const message = this.translation.translate('LAYOUT.COMMON.MESSAGE.UNSAVED_CHANGES');

      this.dialogService
        .confirm({
          title: title,
          message: message,
          confirmLabel: this.translation.translate('LAYOUT.COMMON.BUTTON.LEAVE_WITHOUT_SAVING'),
          cancelLabel: this.translation.translate('LAYOUT.COMMON.BUTTON.CANCEL'),
          variant: 'warning',
        })
        .subscribe({
          next: (confirmed: boolean) => {
            resolve(confirmed);
          },
          error: () => {
            resolve(false);
          },
        });
    });
  }

  /**
   *  Obtiene el ID de la pestaña actual PRIORIZANDO EL TabIdService
   */
  private getCurrentTabId(): string {
    try {
      const serviceTabId = this.tabIdService.getTabId();
      if (serviceTabId && serviceTabId.trim() !== '') {
        return serviceTabId;
      }

      const activeTabId = this.navService.activeTabId();
      if (activeTabId) {
        const tabId = String(activeTabId);
        return tabId;
      }

      const url = this.router.url;
      return url || 'default-tab';
    } catch (error) {
      return 'default-tab';
    }
  }

  // ========================================
  // NG DESTROY
  // ========================================
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
